import { LOGIN_LINK_ENDPOINTS } from '@/constants/auth-endpoints';
import { getValidationLinkErrorReason } from '@/features/auth/utils/auth-error.utils';
import { sessionHintService } from '@/features/auth/services/session-hint-service';
import { tokenRefreshManager } from '@/features/auth/services/token-refresh-manager';
import { makeRequest } from '@/lib/api/http-client';
import { getLogger } from '@/lib/logger';
import { useAuthStore } from '@/stores/auth-store';
import { useLoginRequestStore } from '@/stores/login-request-store';
import { authUserSchema } from '@/types';
import { ValidateLoginLinkResponse } from '@backendTypes/authentication/authentication.schema';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

const logger = getLogger({ scope: 'auth.verify-link' });

const verifyLinkSearchSchema = z.object({ token: z.string().min(1) });

export const Route = createFileRoute('/_public/verify-link')({
  validateSearch: (search) => verifyLinkSearchSchema.parse(search),
  beforeLoad: ({ search }) => ({ token: String(search.token) }),
  loader: async ({ context: { queryClient, token } }) => {
    try {
      const response = await queryClient.fetchQuery<ValidateLoginLinkResponse, unknown>({
        queryKey: ['verify-login-link', token],
        queryFn: async (): Promise<ValidateLoginLinkResponse> => {
          const result = await makeRequest<ValidateLoginLinkResponse>(
            {
              method: 'POST',
              url: LOGIN_LINK_ENDPOINTS.VALIDATE,
              data: { token },
              skipAuthRetry: true,
            },
            { retries: 0 }
          );

          return result.data;
        },
      });

      const parsed = ValidateLoginLinkResponse.safeParse(response);

      if (!parsed.success || !parsed.data.user) {
        if (!parsed.success) {
          logger.error('Validate Login Link backend response shape mismatch', {
            error: parsed.error,
            attributes: {
              'http.endpoint': LOGIN_LINK_ENDPOINTS.VALIDATE,
              'schema.name': 'ValidateLoginLinkResponse',
            },
          });
        }
        throw new Error('Invalid validate login link response');
      }

      const userParsed = authUserSchema.safeParse(parsed.data.user);
      if (!userParsed.success) {
        logger.error('Validate Login Link user data backend response shape mismatch', {
          error: userParsed.error,
          attributes: {
            'http.endpoint': LOGIN_LINK_ENDPOINTS.VALIDATE,
            'schema.name': 'authUserSchema',
          },
        });
        throw new Error('Invalid user data in validate login link response');
      }

      useAuthStore.getState().setAuthenticated(userParsed.data);
      sessionHintService.set();
      if (parsed.data.expiresIn) {
        tokenRefreshManager.setExpiresIn(parsed.data.expiresIn);
      }

      tokenRefreshManager.startAutoRefresh();
    } catch (error) {
      const reason = getValidationLinkErrorReason(error);
      throw redirect({ to: '/login', search: { reason } });
    } finally {
      useLoginRequestStore.getState().clearLinkRequest();
    }

    throw redirect({ to: '/users', replace: true });
  },
  component: () => null,
  pendingComponent: () => (
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
  ),
});
